import { z } from "zod/v4";

import isEmail from "@goose/utils/isEmail";

/**
 * Branded Zod types for Salesforce identifiers and compact user/org records.
 * Base Id validators only require a non-empty string. Strict variants add the
 * 18-character and three-character prefix constraints for boundary validation.
 *
 * **Drizzle ↔ Apex** — Columns in `packages/db/src/schema/salesforce.ts` are
 * filled from Salesforce runtime APIs. Mapping:
 *
 * `salesforce_organizations`:
 * - `id` → `UserInfo.getOrganizationId()`
 * - `name` → `UserInfo.getOrganizationName()`
 * - `url` → `Url.getOrgDomainUrl()`
 *
 * `salesforce_users`:
 * - `organizationId` → `UserInfo.getOrganizationId()`
 * - `id` → `UserInfo.getUserId()`
 * - `name` → `UserInfo.getName()`
 * - `email` → `UserInfo.getEmail()`
 * - `username` → `UserInfo.getUsername()`
 *
 * `SalesforceUserSchema` omits `organizationId`; that column is still typed as
 * {@link OrganizationId} on the `salesforce_users` table for the org that
 * scopes the user row.
 */

const STRICT_LENGTH_ERROR = "Salesforce Id must be exactly 18 characters.";

export const AccountId = z
  .string()
  .min(1)
  .brand("SalesforceAccountId")
  .describe("Non-empty Salesforce Account Id.")
  .meta({ ref: "AccountId" });
export type AccountId = z.infer<typeof AccountId>;

export const StrictAccountId = AccountId.startsWith("001", {
  error: "Salesforce Id must start with `001`.",
})
  .length(18, {
    error: STRICT_LENGTH_ERROR,
  })
  .describe("18-character Salesforce Account Id (prefix `001`).")
  .meta({ ref: "StrictAccountId" });
export type StrictAccountId = z.infer<typeof StrictAccountId>;

export const ContactId = z
  .string()
  .min(1)
  .brand("SalesforceContactId")
  .describe("Non-empty Salesforce Contact Id.")
  .meta({ ref: "ContactId" });
export type ContactId = z.infer<typeof ContactId>;

export const StrictContactId = ContactId.startsWith("003", {
  error: "Salesforce Id must start with `003`.",
})
  .length(18, {
    error: STRICT_LENGTH_ERROR,
  })
  .describe("18-character Salesforce Contact Id (prefix `003`).")
  .meta({ ref: "StrictContactId" });
export type StrictContactId = z.infer<typeof StrictContactId>;

export const EventId = z
  .string()
  .min(1)
  .brand("SalesforceEventId")
  .describe("Non-empty Salesforce Event Id.")
  .meta({ ref: "EventId" });
export type EventId = z.infer<typeof EventId>;

export const StrictEventId = EventId.startsWith("00U", {
  error: "Salesforce Id must start with `00U`.",
})
  .length(18, {
    error: STRICT_LENGTH_ERROR,
  })
  .describe("18-character Salesforce Event Id (prefix `00U`).")
  .meta({ ref: "StrictEventId" });
export type StrictEventId = z.infer<typeof StrictEventId>;

export const OpportunityId = z
  .string()
  .min(1)
  .brand("SalesforceOpportunityId")
  .describe("Non-empty Salesforce Opportunity Id.")
  .meta({ ref: "OpportunityId" });
export type OpportunityId = z.infer<typeof OpportunityId>;

export const StrictOpportunityId = OpportunityId.startsWith("006", {
  error: "Salesforce Id must start with `006`.",
})
  .length(18, {
    error: STRICT_LENGTH_ERROR,
  })
  .describe("18-character Salesforce Opportunity Id (prefix `006`).")
  .meta({ ref: "StrictOpportunityId" });
export type StrictOpportunityId = z.infer<typeof StrictOpportunityId>;

export const OrganizationId = z
  .string()
  .min(1)
  .brand("SalesforceOrganizationId")
  .describe(
    "Non-empty Salesforce organization Id. Same value as Apex `UserInfo.getOrganizationId()`.",
  )
  .meta({ ref: "OrganizationId" });
export type OrganizationId = z.infer<typeof OrganizationId>;

export const StrictOrganizationId = OrganizationId.startsWith("00D", {
  error: "Salesforce Id must start with `00D`.",
})
  .length(18, {
    error: STRICT_LENGTH_ERROR,
  })
  .describe(
    "18-character Salesforce organization Id (prefix `00D`, org record). Same value as Apex `UserInfo.getOrganizationId()`.",
  )
  .meta({ ref: "StrictOrganizationId" });
export type StrictOrganizationId = z.infer<typeof StrictOrganizationId>;

export const TaskId = z
  .string()
  .min(1)
  .brand("SalesforceTaskId")
  .describe("Non-empty Salesforce Task Id.")
  .meta({ ref: "TaskId" });
export type TaskId = z.infer<typeof TaskId>;

export const StrictTaskId = TaskId.startsWith("00T", {
  error: "Salesforce Id must start with `00T`.",
})
  .length(18, {
    error: STRICT_LENGTH_ERROR,
  })
  .describe("18-character Salesforce Task Id (prefix `00T`).")
  .meta({ ref: "StrictTaskId" });
export type StrictTaskId = z.infer<typeof StrictTaskId>;

export const UserId = z
  .string()
  .min(1)
  .brand("SalesforceUserId")
  .describe(
    "Non-empty Salesforce User Id. Same value as Apex `UserInfo.getUserId()`.",
  )
  .meta({ ref: "UserId" });
export type UserId = z.infer<typeof UserId>;

export const StrictUserId = UserId.startsWith("005", {
  error: "Salesforce Id must start with `005`.",
})
  .length(18, {
    error: STRICT_LENGTH_ERROR,
  })
  .describe(
    "18-character Salesforce User Id (prefix `005`). Same value as Apex `UserInfo.getUserId()`.",
  )
  .meta({ ref: "StrictUserId" });
export type StrictUserId = z.infer<typeof StrictUserId>;

export const Username = z
  .string()
  .min(1)
  .brand("SalesforceUsername")
  .describe(
    "Salesforce login username. Same value as Apex `UserInfo.getUsername()`.",
  )
  .meta({ ref: "Username" });
export type Username = z.infer<typeof Username>;

/**
 * Validates an email address, permitting Salesforce org-style addresses whose
 * domain is a bare org id without a TLD (e.g. `noreply@00dfn00000bzlfhean`).
 * `requireTld` is disabled so those pass; standard addresses still validate.
 */
export function isValidEmail(value: string): boolean {
  return isEmail(value, { requireTld: false });
}

export const Email = z
  .string()
  .refine(isValidEmail, { error: "Invalid email address." })
  .brand("SalesforceEmail")
  .describe(
    "Email address on a user's profile. Same value as Apex `UserInfo.getEmail()`.",
  )
  .meta({ ref: "Email" });

export type Email = z.infer<typeof Email>;

export const SalesforceOrganizationSchema = z
  .object({
    id: OrganizationId,
    name: z
      .string()
      .describe(
        "Organization display name. Same value as Apex `UserInfo.getOrganizationName()`.",
      ),
    url: z
      .string()
      .describe(
        "Canonical org base URL. Same value as Apex `Url.getOrgDomainUrl()`.",
      ),
  })
  .describe("Salesforce organization record.")
  .meta({ ref: "SalesforceOrganization" });

export type SalesforceOrganization = z.infer<
  typeof SalesforceOrganizationSchema
>;

export const StrictSalesforceOrganizationSchema =
  SalesforceOrganizationSchema.extend({
    id: StrictOrganizationId,
  })
    .describe("Salesforce organization record with strict Id validation.")
    .meta({ ref: "StrictSalesforceOrganization" });

export type StrictSalesforceOrganization = z.infer<
  typeof StrictSalesforceOrganizationSchema
>;

export const SalesforceUserSchema = z
  .object({
    id: UserId,
    name: z
      .string()
      .min(1)
      .describe(
        "User display name. Same value as Apex `UserInfo.getName()` for the synced user.",
      ),
    email: Email,
    username: Username,
  })
  .describe("Salesforce user identity.")
  .meta({ ref: "SalesforceUser" });

export type SalesforceUser = z.infer<typeof SalesforceUserSchema>;

export const StrictSalesforceUserSchema = SalesforceUserSchema.extend({
  id: StrictUserId,
})
  .describe("Salesforce user identity with strict Id validation.")
  .meta({ ref: "StrictSalesforceUser" });

export type StrictSalesforceUser = z.infer<typeof StrictSalesforceUserSchema>;
